import React, { useRef, useEffect, useState } from 'react';
import { motion, useInView } from 'framer-motion';

const VideoSection = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const playPromiseRef = useRef<Promise<void>>();

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  useEffect(() => {
    if (!videoRef.current || !isMounted) return;

    const video = videoRef.current;
    video.muted = true;
    video.playsInline = true;

    const handlePlay = async () => {
      try {
        if (isInView && video.paused && isMounted) {
          playPromiseRef.current = video.play();
          await playPromiseRef.current;
        }
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') {
          console.error('Error playing video:', error);
        }
      }
    };

    const handlePause = () => {
      if (!isInView && !video.paused) {
        video.pause();
      }
    };

    if (isInView) {
      handlePlay();
    } else {
      handlePause();
    }

    return () => {
      if (playPromiseRef.current) {
        playPromiseRef.current
          .then(() => {
            if (video && !video.paused) {
              video.pause();
            }
          })
          .catch(() => {});
      }
    };
  }, [isInView, isMounted]);

  return (
    <div ref={containerRef} className="relative h-screen">
      <div className="absolute inset-0 w-full h-full">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: isLoaded ? 1 : 0 }}
          transition={{ duration: 0.5 }}
          className="relative w-full h-full"
        >
          <video 
            ref={videoRef}
            loop 
            muted 
            playsInline 
            className="w-full h-full object-cover"
            preload="metadata"
            onLoadedData={() => setIsLoaded(true)}
          >
            <source 
              src="https://res.cloudinary.com/dansvy9a5/video/upload/v1743093879/clip_website_devckh.mov" 
              type="video/mp4"
            />
            Your browser does not support video playback.
          </video>
          <div className="absolute inset-0 bg-black/50" />
        </motion.div>
        
        {/* Loading placeholder */}
        {!isLoaded && (
          <div className="absolute inset-0 bg-black flex items-center justify-center">
            <div className="w-16 h-16 border-4 border-[#c9ab81] border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoSection;